'use client';

import { useEffect, useState } from 'react';
import type { NavTourStep } from './navTourSteps';
import { clampNavTourBubblePosition, type NavTourAnchorRect } from './navTourAnchor';

type Props = {
  step: NavTourStep;
  stepIndex: number;
  stepCount: number;
  anchorSelector: string;
  onBack: () => void;
  onNext: () => void;
  onSkip: () => void;
};

export function NavTourBubble({ step, stepIndex, stepCount, anchorSelector, onBack, onNext, onSkip }: Props) {
  const [anchor, setAnchor] = useState<NavTourAnchorRect | null>(null);

  useEffect(() => {
    function measure() {
      const el = document.querySelector(anchorSelector);
      if (!el) {
        setAnchor(null);
        return;
      }
      el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      const r = el.getBoundingClientRect();
      setAnchor({ top: r.top, left: r.left, width: r.width, height: r.height });
    }
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    const t = window.setInterval(measure, 400);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
      window.clearInterval(t);
    };
  }, [anchorSelector, step.id]);

  const bubbleStyle: React.CSSProperties = anchor
    ? clampNavTourBubblePosition(anchor, {
        width: typeof window !== 'undefined' ? window.innerWidth : 1280,
        height: typeof window !== 'undefined' ? window.innerHeight : 720,
      })
    : { top: '20%', left: '50%', transform: 'translateX(-50%)' };

  return (
    <div className="nav-tour" data-testid="nav-tour-overlay" role="presentation">
      <div className="nav-tour__scrim" aria-hidden />
      {anchor ? (
        <div
          className="nav-tour__spotlight"
          style={{
            top: anchor.top - 4,
            left: anchor.left - 4,
            width: anchor.width + 8,
            height: anchor.height + 8,
          }}
        />
      ) : null}
      <div className="nav-tour__bubble" style={bubbleStyle} role="dialog" aria-labelledby="nav-tour-title">
        <p className="nav-tour__eyebrow">
          Step {stepIndex + 1} of {stepCount}
        </p>
        <h2 id="nav-tour-title" className="nav-tour__title">
          {step.title}
        </h2>
        <p className="nav-tour__body">{step.body}</p>
        <div className="nav-tour__actions">
          <button type="button" className="nav-tour__btn nav-tour__btn--ghost" onClick={onSkip}>
            Skip
          </button>
          {stepIndex > 0 ? (
            <button type="button" className="nav-tour__btn nav-tour__btn--ghost" onClick={onBack}>
              Back
            </button>
          ) : null}
          <button type="button" className="nav-tour__btn nav-tour__btn--primary" onClick={onNext}>
            {stepIndex >= stepCount - 1 ? 'Done' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}
